import React, { useEffect, useState } from 'react';
import { CloseButton } from '@fiverr-private/button';
import { Container, Stack } from '@fiverr-private/layout_components';
import { I18n } from '@fiverr-private/i18n-react';
import { Link, Text } from '@fiverr-private/typography';
import { Image } from '@fiverr-private/media';
import { ModalType, openFipMigrationModal } from '@fiverr-private/pro_migration';
import { LoyaltyProgramTier } from '@fiverr-private/loyalty_utils';
import {
    reportJoinProRewardsBannerClick,
    reportJoinProRewardsBannerCloseClick,
    reportJoinProRewardsBannerImpression,
} from '@fiverr-private/listing_events';
import {
    LOYALTY_UPGRADE_TO_PRO_BANNER_SHOWN_ON_LISTINGS_AND_EXPIRE_AFTER,
    THREE_MONTHS_IN_MILLISECONDS,
} from './consts';

const T_PREFIX = 'search_perseus.join_pro_rewards_program';
const STARS_IMAGE_SRC =
    'https://fiverr-res.cloudinary.com/image/upload/f_auto,q_auto/v1/attachments/generic_asset/asset/f010590151bec8f0a9ba661315e4fe57-1744805887577/Loyalty-program-stars%282x%29.png';
const STARS_IMAGE_MINI_SRC =
    'https://fiverr-res.cloudinary.com/image/upload/f_auto,q_auto/v1/attachments/generic_asset/asset/d503e36025ab67c2f271e13563a22499-1744807979711/Loyalty-program-stars-mini%282x%29.png';
const GOLD_POINTS_IMAGE_SRC =
    'https://fiverr-res.cloudinary.com/image/upload/f_png,q_auto/v1/attachments/generic_asset/asset/4d8bdbb7fcb0cd2741a4fa5e1aa182d8-1748951378417/Gold_Points.svg';
const PLATINUM_POINTS_IMAGE_SRC =
    'https://fiverr-res.cloudinary.com/image/upload/f_png,q_auto/v1/attachments/generic_asset/asset/2a3b185e97b612f56458a4ca85dbdb00-1748951550143/Platinum_Points.svg';

export interface JoinProRewardsProgramBannerProps {
    tier: LoyaltyProgramTier;
}

export const JoinProRewardsProgramBanner = ({ tier }: JoinProRewardsProgramBannerProps) => {
    const [closed, setClosed] = useState(false);
    const isGoldTier = tier === 'GOLD';
    const isPlatinumTier = tier === 'PLATINUM';
    const pointsImageSrc = isPlatinumTier ? PLATINUM_POINTS_IMAGE_SRC : GOLD_POINTS_IMAGE_SRC;

    const handleCloseClick = () => {
        setClosed(true);
        reportJoinProRewardsBannerCloseClick();
        const currentTimestamp = new Date().getTime();
        const disableDisplayExpiresAfter = currentTimestamp + THREE_MONTHS_IN_MILLISECONDS;
        localStorage.setItem(
            LOYALTY_UPGRADE_TO_PRO_BANNER_SHOWN_ON_LISTINGS_AND_EXPIRE_AFTER,
            String(disableDisplayExpiresAfter)
        );
    };
    const handleJoinClick = () => {
        let source = '';
        let pageElementName = '';
        if (isGoldTier) {
            source = 'existing_gold_eligibility_seamless_migration_modal_listing';
            pageElementName = 'existing_gold_eligibility_seamless_migration_modal_listing';
        } else if (isPlatinumTier) {
            source = 'existing_platinum_eligibility_seamless_migration_modal_listing';
            pageElementName = 'existing_platinum_eligibility_seamless_migration_modal_listing';
        }
        reportJoinProRewardsBannerClick();
        openFipMigrationModal({
            variant: ModalType.LoyaltyUpgradeToPro,
            payload: {
                source,
                biEnrichment: { pageElementName },
                tierContext: {
                    tier,
                    tierAlreadyReached: true,
                },
            },
        });
    };

    useEffect(() => {
        reportJoinProRewardsBannerImpression();
        // @disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (closed) {
        return null;
    }

    return (
        <Container
            position="relative"
            width="100%"
            display="flex"
            justifyContent="center"
            borderRadius="lg"
            paddingY="4"
            paddingLeft={{ default: '4', sm: '16' }}
            paddingRight="9"
            style={{ background: 'linear-gradient(180deg, #003511 0%, #01260C 100%)' }}
        >
            <Image
                width="132px"
                height="42px"
                position="absolute"
                left="16px"
                top="16px"
                alt="stars"
                display={{ default: 'none', sm: 'block' }}
                src={{ default: STARS_IMAGE_MINI_SRC, lg: { srcSet: STARS_IMAGE_SRC } }}
            />
            <Stack
                direction="row"
                alignItems={{
                    default: 'flexStart',
                    sm: 'center',
                }}
                gap="2"
                minHeight="42px"
            >
                <Container display="flex" alignItems="center" height="24px">
                    <Image alt="points" width="36px" height="20px" src={pointsImageSrc} />
                </Container>
                <Text color="white">
                    <I18n k={`${T_PREFIX}.title`} />
                    <Link
                        color="white"
                        fontSize="b_sm"
                        fontWeight="bold"
                        cursor="pointer"
                        marginLeft="1"
                        onClick={handleJoinClick}
                    >
                        <I18n k={`${T_PREFIX}.cta`} />
                    </Link>
                </Text>
            </Stack>
            <Image
                width="132px"
                height="42px"
                position="absolute"
                right="40px"
                top="16px"
                alt="stars"
                transform="rotateY(180deg)"
                display={{ default: 'none', md: 'block' }}
                src={{ default: STARS_IMAGE_MINI_SRC, lg: { srcSet: STARS_IMAGE_SRC } }}
            />
            <Container
                position="absolute"
                right="4px"
                top="50%"
                transform="translateY(-50%)"
                display={{
                    default: 'none',
                    sm: 'block',
                }}
            >
                <CloseButton size="sm" color="light" onClick={handleCloseClick} />
            </Container>
            <Container
                position="absolute"
                right="4px"
                top="8px"
                display={{
                    default: 'block',
                    sm: 'none',
                }}
            >
                <CloseButton size="sm" color="light" onClick={handleCloseClick} />
            </Container>
        </Container>
    );
};
